/*
Universal search sheet — opens from the map's 🔍 FAB and routes the
user to one of three destinations:

  - Species (commonName / Latin binomial match) → species detail screen
  - Arrondissement (1–20, label or number) → fly the map to the centroid
  - Arbre OpenData id (numeric prefix match) → fly + open the bottom
    sheet for that arbre

The sheet is appended directly under `<body>` so it survives the
router's per-screen `innerHTML = ''` swaps. It builds its own DOM,
focuses the input, listens for input + click + key events, and
returns a close function for callers that want to dismiss
imperatively (e.g. on result selection).

Search is debounced (60 ms) — well below the perceptible
keystroke→update lag — and runs synchronously over in-memory data:
the species reverse map (~909 entries) and the lightweight
arbresFeatures projection (~213 k entries). On a phone the species
scan is ~1-2 ms; the arbre-by-id scan is short-circuited to a Map
lookup so it stays sub-ms even as the dataset grows.
*/

import { allSpecies, ensureSpeciesIndex, type SpeciesName } from '../../data/speciesIndex'
import { ensureArbresFeatures, type ArbreFeature } from '../../data/arbresFeatures'

export interface ArrondissementInfo
{
	arr: number   // 1..20
	label: string // "1er", "2e", …, "20e"
	lat: number   // approximate centroid latitude (degrees)
	lon: number   // approximate centroid longitude (degrees)
}

export interface SearchSheetCallbacks
{
	onSpecies: (inSk: number) => void
	onArrondissement: (inInfo: ArrondissementInfo) => void
	onArbre: (inFeature: ArbreFeature) => void
}

// Approximate centroids of Paris arrondissements. Coordinates from
// the well-known 20 arrondissement boundaries (public-domain — the
// arrondissements are administrative units, no licensing). Values
// here are good to ~50 m, more than enough for "fly to the area".
const kArrondissements: readonly ArrondissementInfo[] = [
	{ arr: 1,  label: '1er',  lat: 48.8625, lon: 2.3360 },
	{ arr: 2,  label: '2e',   lat: 48.8682, lon: 2.3411 },
	{ arr: 3,  label: '3e',   lat: 48.8631, lon: 2.3624 },
	{ arr: 4,  label: '4e',   lat: 48.8546, lon: 2.3577 },
	{ arr: 5,  label: '5e',   lat: 48.8462, lon: 2.3441 },
	{ arr: 6,  label: '6e',   lat: 48.8488, lon: 2.3329 },
	{ arr: 7,  label: '7e',   lat: 48.8568, lon: 2.3122 },
	{ arr: 8,  label: '8e',   lat: 48.8728, lon: 2.3122 },
	{ arr: 9,  label: '9e',   lat: 48.8770, lon: 2.3373 },
	{ arr: 10, label: '10e',  lat: 48.8761, lon: 2.3601 },
	{ arr: 11, label: '11e',  lat: 48.8593, lon: 2.3781 },
	{ arr: 12, label: '12e',  lat: 48.8407, lon: 2.3884 },
	{ arr: 13, label: '13e',  lat: 48.8326, lon: 2.3624 },
	{ arr: 14, label: '14e',  lat: 48.8331, lon: 2.3265 },
	{ arr: 15, label: '15e',  lat: 48.8417, lon: 2.2998 },
	{ arr: 16, label: '16e',  lat: 48.8603, lon: 2.2620 },
	{ arr: 17, label: '17e',  lat: 48.8848, lon: 2.3070 },
	{ arr: 18, label: '18e',  lat: 48.8927, lon: 2.3445 },
	{ arr: 19, label: '19e',  lat: 48.8836, lon: 2.3851 },
	{ arr: 20, label: '20e',  lat: 48.8633, lon: 2.4022 },
]

// Per-kind result caps. The arbre-id list grows fast on a short
// numeric prefix ("1" matches everything starting with 1), so we cap
// it harder; the species list is bounded by the catalogue size and
// rarely needs more than the top dozen.
const kSpeciesCap: number = 12
const kArbreCap: number = 8
const kArrondissementCap: number = 5
const kSearchDebounceMs: number = 60  // tight enough that fast typists never see "missed" updates

/*
Open the search sheet. Builds the DOM, attaches handlers, returns a
close function. Idempotent at the DOM level: if a previous sheet is
still mounted (rare — the close function destroys it), we close
that one before mounting a fresh one.
*/
export function openSearchSheet(inCallbacks: SearchSheetCallbacks): () => void
{
	const vExisting = document.getElementById('search-overlay')
	if (vExisting) vExisting.remove()

	const vRoot = document.createElement('div')
	vRoot.id = 'search-overlay'
	vRoot.className = 'search-overlay'
	vRoot.setAttribute('role', 'dialog')
	vRoot.setAttribute('aria-modal', 'true')
	vRoot.innerHTML = `
		<div class="search-panel">
			<header class="search-header">
				<span class="search-icon" aria-hidden="true">🔍</span>
				<input class="search-input" id="search-input" type="search" inputmode="search" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" placeholder="Espèce, arrondissement, id…" aria-label="Rechercher" />
				<button class="search-close" type="button" aria-label="Fermer">×</button>
			</header>
			<div class="search-results" id="search-results" role="listbox">
				<div class="search-hint">Tape un nom d'espèce, un arrondissement (1er…20e), ou un id OpenData.</div>
			</div>
		</div>
	`
	document.body.appendChild(vRoot)

	const vInput = vRoot.querySelector<HTMLInputElement>('#search-input')
	const vResults = vRoot.querySelector<HTMLElement>('#search-results')
	const vCloseBtn = vRoot.querySelector<HTMLButtonElement>('.search-close')

	let vClosed: boolean = false  // single-fire guard so multiple close paths don't race
	let vDebounceTimer: number | null = null  // pending debounce window for the input handler

	const vClose = (): void => {
		if (vClosed) return
		vClosed = true
		if (vDebounceTimer !== null) window.clearTimeout(vDebounceTimer)
		document.removeEventListener('keydown', vOnKey)
		vRoot.remove()
	}

	const vOnKey = (inEv: KeyboardEvent): void => {
		if (inEv.key === 'Escape') vClose()
	}
	document.addEventListener('keydown', vOnKey)

	vCloseBtn?.addEventListener('click', vClose)

	// Tap outside the panel closes — the panel is full-width on
	// mobile, but on desktop it sits centred so background taps work.
	vRoot.addEventListener('click', (inEv) => {
		if (inEv.target === vRoot) vClose()
	})

	// Pre-warm the indices in parallel; we render whatever's available
	// at each keystroke and ignore late arrivals (the input event
	// re-runs after the data is ready).
	let vSpeciesReady = false
	let vFeaturesReady = false
	let vFeatures: ArbreFeature[] = []
	let vFeaturesById: Map<number, ArbreFeature> = new Map()  // id-keyed for arbre-id exact lookups
	void ensureSpeciesIndex().then(() => { vSpeciesReady = true; if (vInput?.value) vRender(vInput.value) }).catch(() => { /* soft */ })
	void ensureArbresFeatures().then((inList) => {
		vFeatures = inList
		vFeaturesById = new Map(inList.map((inF) => [inF.id, inF]))
		vFeaturesReady = true
		if (vInput?.value) vRender(vInput.value)
	}).catch(() => { /* soft */ })

	const vRender = (inQuery: string): void => {
		if (!vResults) return
		const vTrim = inQuery.trim()
		if (vTrim.length === 0)
		{
			vResults.innerHTML = `<div class="search-hint">Tape un nom d'espèce, un arrondissement (1er…20e), ou un id OpenData.</div>`
			return
		}
		const vSections: string[] = []  // accumulated HTML, joined at the end so we paint once

		const vIsNumeric = /^\d+$/.test(vTrim)
		if (vIsNumeric)
		{
			// Numeric path: prefer arbre-id matches first (the user
			// most likely means an OpenData id when they type pure
			// digits); also surface arrondissement matches because
			// "11" is also "11e arrondissement".
			vSections.push(renderArbreSection(vTrim, vFeatures, vFeaturesById, vFeaturesReady))
			vSections.push(renderArrondissementSection(vTrim))
		}
		else
		{
			// Text path: species first (most common intent for a name
			// query), arrondissement second (covers "1er", "2eme",
			// "Bastille" — wait no, we don't have boroughs by name yet,
			// just numeric labels).
			vSections.push(renderSpeciesSection(vTrim, vSpeciesReady))
			vSections.push(renderArrondissementSection(vTrim))
		}

		const vMarkup = vSections.filter((inS) => inS.length > 0).join('')
		vResults.innerHTML = vMarkup.length > 0
			? vMarkup
			: `<div class="search-hint search-hint-empty">Aucun résultat pour « ${escapeHtml(vTrim)} ».</div>`

		wireResultClicks(vResults, vFeaturesById, inCallbacks, vClose)
	}

	vInput?.addEventListener('input', () => {
		const vQuery = vInput.value
		if (vDebounceTimer !== null) window.clearTimeout(vDebounceTimer)
		vDebounceTimer = window.setTimeout(() => {
			vDebounceTimer = null
			vRender(vQuery)
		}, kSearchDebounceMs)
	})

	// Pressing Enter in the input triggers the first result if any.
	vInput?.addEventListener('keydown', (inEv: KeyboardEvent) => {
		if (inEv.key !== 'Enter') return
		const vFirst = vResults?.querySelector<HTMLElement>('.search-result')
		vFirst?.click()
	})

	// Auto-focus on mount. iOS Safari requires the input to be in the
	// DOM and visible before focus() works; we're already past both.
	window.setTimeout(() => vInput?.focus(), 0)

	return vClose
}

/*
Render the species section — exact common-name match first, then
common-name prefix, then any-substring across common name + Latin
binomial. Stops at kSpeciesCap entries.
*/
function renderSpeciesSection(inQuery: string, inReady: boolean): string
{
	if (!inReady) return ''
	const vNeedle = inQuery.toLowerCase()
	const vAll = allSpecies()
	const vExact: typeof vAll = []          // common-name === query
	const vPrefix: typeof vAll = []          // common-name OR latin starts with query
	const vSubstring: typeof vAll = []       // anywhere in common-name OR latin

	for (const vEntry of vAll)
	{
		const vCommon = (vEntry.name.commonName ?? '').toLowerCase()
		const vLatin = formatLatin(vEntry.name).toLowerCase()
		if (vCommon === vNeedle) vExact.push(vEntry)
		else if (vCommon.startsWith(vNeedle) || vLatin.startsWith(vNeedle)) vPrefix.push(vEntry)
		else if (vCommon.includes(vNeedle) || vLatin.includes(vNeedle)) vSubstring.push(vEntry)
	}
	const vRanked = [...vExact, ...vPrefix, ...vSubstring].slice(0, kSpeciesCap)
	if (vRanked.length === 0) return ''

	const vRows = vRanked.map((inEntry) => {
		const vTitle = inEntry.name.commonName ?? formatLatin(inEntry.name)
		const vSubtitle = inEntry.name.commonName ? formatLatin(inEntry.name) : ''
		return `
			<button class="search-result" type="button" data-kind="species" data-sk="${inEntry.sk}">
				<span class="search-result-glyph" aria-hidden="true">🌿</span>
				<span class="search-result-body">
					<span class="search-result-title">${escapeHtml(vTitle)}</span>
					${vSubtitle ? `<span class="search-result-subtitle"><em>${escapeHtml(vSubtitle)}</em></span>` : ''}
				</span>
			</button>
		`
	}).join('')
	return `<div class="search-section"><div class="search-section-label">Espèces</div>${vRows}</div>`
}

/*
Render the arrondissement section. Matches numeric prefix ("1" → 1, 10–19),
ordinal labels ("1er", "12e"), and Paris-prefixed forms ("75001",
"75012"). Stops at kArrondissementCap entries.
*/
function renderArrondissementSection(inQuery: string): string
{
	const vNeedle = inQuery.toLowerCase()
	const vMatches: ArrondissementInfo[] = []
	for (const vArr of kArrondissements)
	{
		const vNum = String(vArr.arr)               // "1", "12", "20"
		const vPad = vNum.padStart(2, '0')          // "01", "12", "20"
		const vPostal = `750${vPad}`                // "75001", "75012", "75020"
		const vLabel = vArr.label.toLowerCase()     // "1er", "2e", …
		// Match if any of these forms starts with the needle.
		if (
			vNum.startsWith(vNeedle)
			|| vPad.startsWith(vNeedle)
			|| vPostal.startsWith(vNeedle)
			|| vLabel.startsWith(vNeedle)
			|| vNeedle === `${vNum}er`
			|| vNeedle === `${vNum}e`
			|| vNeedle === `${vNum}eme`
			|| vNeedle === `${vNum}ème`
		)
		{
			vMatches.push(vArr)
		}
	}
	const vCapped = vMatches.slice(0, kArrondissementCap)
	if (vCapped.length === 0) return ''

	const vRows = vCapped.map((inArr) => `
		<button class="search-result" type="button" data-kind="arr" data-arr="${inArr.arr}">
			<span class="search-result-glyph" aria-hidden="true">🗺️</span>
			<span class="search-result-body">
				<span class="search-result-title">${escapeHtml(inArr.label)} arrondissement</span>
				<span class="search-result-subtitle">Paris ${String(inArr.arr).padStart(2, '0')}</span>
			</span>
		</button>
	`).join('')
	return `<div class="search-section"><div class="search-section-label">Arrondissements</div>${vRows}</div>`
}

/*
Render the arbre-id section. Exact id match first, then a prefix
scan capped at kArbreCap to keep the linear pass cheap on the 213k
features array. The exact-match shortcut uses the id-keyed map for
sub-ms resolution.
*/
function renderArbreSection(
	inQuery: string,
	inAll: ArbreFeature[],
	inById: Map<number, ArbreFeature>,
	inReady: boolean,
): string
{
	if (!inReady) return ''
	const vId = Number(inQuery)
	const vMatches: ArbreFeature[] = []
	if (Number.isFinite(vId))
	{
		const vExact = inById.get(vId)
		if (vExact) vMatches.push(vExact)
	}
	// Prefix scan over the array. We stop once we have kArbreCap +
	// (1 if exact already matched) results so the worst-case scan is
	// bounded — for a 1-digit query that hits every id, this stops
	// in the first dozen iterations.
	const vCap = kArbreCap + vMatches.length
	for (const vFeature of inAll)
	{
		if (vMatches.length >= vCap) break
		if (vFeature.id === vMatches[0]?.id) continue  // skip the exact match we already pushed
		const vIdStr = String(vFeature.id)
		if (vIdStr.startsWith(inQuery)) vMatches.push(vFeature)
	}
	if (vMatches.length === 0) return ''

	const vRows = vMatches.slice(0, kArbreCap).map((inF) => `
		<button class="search-result" type="button" data-kind="arbre" data-arbre-id="${inF.id}">
			<span class="search-result-glyph" aria-hidden="true">🌳</span>
			<span class="search-result-body">
				<span class="search-result-title">Arbre #${inF.id}</span>
				<span class="search-result-subtitle">${inF.remarquable ? 'Remarquable · ' : ''}${inF.lat.toFixed(4)}, ${inF.lon.toFixed(4)}</span>
			</span>
		</button>
	`).join('')
	return `<div class="search-section"><div class="search-section-label">Arbres (id OpenData)</div>${vRows}</div>`
}

/*
Wire click handlers on the freshly-rendered result rows. Called after
each render() so the listeners are bound to the current DOM (the
results container's innerHTML is rewritten on every keystroke).
*/
function wireResultClicks(
	inResults: HTMLElement,
	inFeaturesById: Map<number, ArbreFeature>,
	inCallbacks: SearchSheetCallbacks,
	inClose: () => void,
): void
{
	inResults.querySelectorAll<HTMLElement>('.search-result').forEach((inBtn) => {
		inBtn.addEventListener('click', () => {
			const vKind = inBtn.dataset.kind
			if (vKind === 'species')
			{
				const vSk = Number(inBtn.dataset.sk)
				if (Number.isFinite(vSk))
				{
					inClose()
					inCallbacks.onSpecies(vSk)
				}
				return
			}
			if (vKind === 'arr')
			{
				const vArrNum = Number(inBtn.dataset.arr)
				const vInfo = kArrondissements.find((inA) => inA.arr === vArrNum)
				if (vInfo)
				{
					inClose()
					inCallbacks.onArrondissement(vInfo)
				}
				return
			}
			if (vKind === 'arbre')
			{
				const vId = Number(inBtn.dataset.arbreId)
				const vFeat = inFeaturesById.get(vId)
				if (vFeat)
				{
					inClose()
					inCallbacks.onArbre(vFeat)
				}
				return
			}
		})
	})
}

function formatLatin(inName: SpeciesName): string
{
	const vEspece = inName.espece.length > 0 ? ` ${inName.espece}` : ''
	return `${inName.genre}${vEspece}`
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}
